import React from "react";
import { Card, Row, Col, Statistic, Table, Tag, Divider } from "antd";
import { WalletOutlined, DollarOutlined, InfoCircleOutlined } from "@ant-design/icons";

const SystemWalletsFinancial = ({ systemWallets, financial }) => {
  const formatCurrency = (value) => {
    return new Intl.NumberFormat("fr-MA", {
      style: "currency",
      currency: "MAD",
      minimumFractionDigits: 2,
    }).format(value || 0);
  };

  // System wallets columns
  const systemWalletsColumns = [
    {
      title: "Wallet Type",
      dataIndex: "type",
      key: "type",
      render: (text) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: "Balance",
      dataIndex: "balance",
      key: "balance",
      render: (value) => (
        <span className="font-semibold">{formatCurrency(value)}</span>
      ),
    },
    {
      title: "Locked",
      dataIndex: "locked",
      key: "locked",
      render: (value) => formatCurrency(value),
    },
    {
      title: "Available",
      dataIndex: "available",
      key: "available",
      render: (value) => (
        <span className="text-green-600 font-semibold">{formatCurrency(value)}</span>
      ),
    },
  ];

  // Convert system wallets object to table data (values are numeric, not objects)
  const systemWalletsData = Object.entries(systemWallets || {}).map(([key, value], index) => ({
    key: index,
    type: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
    balance: typeof value === 'number' ? value : 0,
    locked: 0, // Not provided in current API
    available: typeof value === 'number' ? value : 0,
  }));

  // Financial reconciliation data
  const financialData = financial || {};

  return (
    <div className="space-y-6">
      {/* System Wallets */}
      <Card
        title={
          <span className="text-lg font-semibold">
            <WalletOutlined className="mr-2 text-blue-600" /> System Wallets
          </span>
        }
        className="hover:shadow-xl transition-all duration-300 border-t-4 border-blue-500"
      >
        <Table
          columns={systemWalletsColumns}
          dataSource={systemWalletsData}
          pagination={false}
          size="middle"
          className="custom-table"
        />
      </Card>

      {/* Financial Reconciliation */}
      <Card
        title={
          <span className="text-lg font-semibold">
            <DollarOutlined className="mr-2 text-green-600" /> Financial Overview
          </span>
        }
        className="hover:shadow-xl transition-all duration-300 border-t-4 border-green-500"
      >
        <Row gutter={[16, 16]}>
          {/* Financial Overview */}
          <Col xs={24}>
            <Divider orientation="left">
              <InfoCircleOutlined /> Financial Overview
            </Divider>
          </Col>
          
          {financialData.total_users_with_wallets !== undefined && (
            <Col xs={12} sm={8} md={6}>
              <Statistic
                title="Users With Wallets"
                value={financialData.total_users_with_wallets}
                valueStyle={{ color: "#1890ff" }}
              />
            </Col>
          )}
          
          {financialData.total_in_wallets !== undefined && (
            <Col xs={12} sm={8} md={6}>
              <Statistic
                title="Total in Wallets"
                value={formatCurrency(financialData.total_in_wallets)}
                valueStyle={{ color: "#52c41a" }}
              />
            </Col>
          )}
          
          {financialData.total_cmi_payments !== undefined && (
            <Col xs={12} sm={8} md={6}>
              <Statistic
                title="Total CMI Payments"
                value={formatCurrency(financialData.total_cmi_payments)}
                valueStyle={{ color: "#722ed1" }}
              />
            </Col>
          )}
          
          {financialData.total_system_revenue !== undefined && (
            <Col xs={12} sm={8} md={6}>
              <Statistic
                title="System Revenue"
                value={formatCurrency(financialData.total_system_revenue)}
                valueStyle={{ color: "#52c41a" }}
              />
            </Col>
          )}

          {/* Token Pool */}
          {(financialData.current_token_pool !== undefined || financialData.initial_token_pool !== undefined) && (
            <>
              <Col xs={24}>
                <Divider orientation="left">
                  <InfoCircleOutlined /> Token Pool
                </Divider>
              </Col>
              {financialData.initial_token_pool !== undefined && (
                <Col xs={12} sm={8} md={6}>
                  <Statistic
                    title="Initial Pool"
                    value={formatCurrency(financialData.initial_token_pool)}
                    valueStyle={{ color: "#1890ff" }}
                  />
                </Col>
              )}
              {financialData.current_token_pool !== undefined && (
                <Col xs={12} sm={8} md={6}>
                  <Statistic
                    title="Current Pool"
                    value={formatCurrency(financialData.current_token_pool)}
                    valueStyle={{ color: "#52c41a" }}
                  />
                </Col>
              )}
              {(financialData.initial_token_pool !== undefined && financialData.current_token_pool !== undefined) && (
                <Col xs={12} sm={8} md={6}>
                  <Statistic
                    title="Pool Change"
                    value={formatCurrency(financialData.initial_token_pool - financialData.current_token_pool)}
                    valueStyle={{ 
                      color: (financialData.initial_token_pool - financialData.current_token_pool) > 0 
                        ? "#52c41a" 
                        : "#ff4d4f" 
                    }}
                  />
                </Col>
              )}
            </>
          )}

          {/* Settlements */}
          {financialData.total_settlements !== undefined && (
            <>
              <Col xs={24}>
                <Divider orientation="left">
                  <InfoCircleOutlined /> Settlements
                </Divider>
              </Col>
              <Col xs={12} sm={8} md={6}>
                <Statistic
                  title="Total Settlements"
                  value={formatCurrency(financialData.total_settlements)}
                  valueStyle={{ color: "#1890ff" }}
                />
              </Col>
            </>
          )}

          {/* Transaction Summary */}
          {financialData.total_transactions !== undefined && (
            <>
              <Col xs={24}>
                <Divider orientation="left">
                  <InfoCircleOutlined /> Transaction Summary
                </Divider>
              </Col>
              <Col xs={12} sm={8} md={6}>
                <Statistic
                  title="Total Transactions"
                  value={financialData.total_transactions}
                  valueStyle={{ color: "#1890ff" }}
                />
              </Col>
              {financialData.completed_transactions !== undefined && (
                <Col xs={12} sm={8} md={6}>
                  <Statistic
                    title="Completed"
                    value={financialData.completed_transactions}
                    valueStyle={{ color: "#52c41a" }}
                  />
                </Col>
              )}
              {financialData.pending_transactions !== undefined && (
                <Col xs={12} sm={8} md={6}>
                  <Statistic
                    title="Pending"
                    value={financialData.pending_transactions}
                    valueStyle={{ color: "#faad14" }}
                  />
                </Col>
              )}
            </>
          )}

        </Row>
      </Card>
    </div>
  );
};

export default SystemWalletsFinancial;
